/**
 * Date formatting, in one place.
 *
 * Every table showed dates its own way — `toLocaleDateString()` on one page, a hand-built
 * month/day/hour/minute on another, a raw ISO string in a third. Scanning two tables meant
 * re-reading two formats. These are the only two shapes the app uses now.
 *
 * All of them take the browser's locale and timezone rather than hardcoding a format, so a
 * reader outside en-US sees their own conventions.
 */

/** Date only: "Aug 27, 2026". For created/expiry columns, where time is noise. */
export function formatDate(value: string | number | Date | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Date and time: "Aug 27, 11:51 AM". For anything where ordering within a day matters. */
export function formatDateTime(value: string | number | Date | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * "just now" / "3m ago" / "2h ago", falling back to an absolute date past a week.
 *
 * Used for "Last seen" and "Last used", where the useful question is "is this recent?" — a
 * reader should not have to subtract timestamps to answer it. Past a week the relative form
 * stops being informative and the absolute date is more useful.
 */
export function formatRelative(value: string | number | Date | null | undefined): string {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never";

  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 0) return formatDateTime(date); // clock skew, or a scheduled future time
  if (seconds < 45) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  if (seconds < 604_800) return `${Math.round(seconds / 86_400)}d ago`;
  return formatDate(date);
}

/** Thousands separators for stat cards, so 12345 reads as 12,345 at a glance. */
export function formatCount(value: number | null | undefined): string {
  if (typeof value !== "number" || Number.isNaN(value)) return "0";
  return value.toLocaleString();
}
